import { useState, useEffect, useMemo } from 'react';
import { fetchCityList, City } from '../api/city';

interface UseCityListReturn {
    cities: City[];
    cityMap: Map<string, string>;
    isLoading: boolean;
    error: string | null;
    refetch: () => void;
}

export const useCityList = (): UseCityListReturn => {
    const [cities, setCities] = useState<City[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchCities = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await fetchCityList();
            
            if (response.status_code === 0) {
                setCities(response.data.cities);
            } else {
                throw new Error(response.status_message || 'Failed to fetch cities');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch cities';
            setError(errorMessage);
            console.error('Error fetching cities:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCities();
    }, []);

    const refetch = () => {
        fetchCities();
    };

    // Create a hashmap for O(1) city name lookup
    const cityMap = useMemo(() => {
        const map = new Map<string, string>();
        if (cities && cities.length > 0) {
            cities.forEach((city) => {
                map.set(city.city_id, city.city_name);
            });
        }
        return map;
    }, [cities]);

    return {
        cities,
        cityMap,
        isLoading,
        error,
        refetch,
    };
};
